import { Component } from '@theme/component';
import { Wishlist } from '@theme/wishlist';
import { WishlistUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * A toggle button that saves/removes a product from the customer's wishlist.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} button
 *
 * @extends {Component<Refs>}
 */
export class WishlistButtonComponent extends Component {
  requiredRefs = ['button'];

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  get productId() {
    return this.dataset.productId ?? '';
  }

  render() {
    const isSaved = this.productId ? Wishlist.has(this.productId) : false;
    this.applyState(isSaved);
  }

  /**
   * Reflects a saved/unsaved state onto the button. Driven by the toggle's
   * return value (not a storage re-read) so the UI responds even when
   * localStorage writes are blocked.
   * @param {boolean} isSaved
   */
  applyState(isSaved) {
    const label = isSaved ? this.dataset.labelRemove : this.dataset.labelSave;

    this.refs.button.setAttribute('aria-pressed', isSaved.toString());
    if (label) this.refs.button.setAttribute('aria-label', label);
    this.dataset.active = isSaved.toString();
  }

  /**
   * Handles a click on the toggle button.
   * @param {Event} event
   */
  toggle(event) {
    event.preventDefault();

    if (!this.productId) return;

    const isSaved = Wishlist.toggle(this.productId);
    this.applyState(isSaved);
    this.dispatchEvent(new WishlistUpdateEvent(this.productId, isSaved));
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButtonComponent);
}

/**
 * Header icon that shows a live count of wishlisted products. Toggle events
 * from any wishlist-button bubble to document, so one listener covers them all.
 *
 * @typedef {Object} HeaderIconRefs
 * @property {HTMLElement} count
 *
 * @extends {Component<HeaderIconRefs>}
 */
export class WishlistHeaderIconComponent extends Component {
  requiredRefs = ['count'];

  #onWishlistUpdate = () => this.renderCount();

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    // pageshow covers back/forward-cache restores where connectedCallback
    // doesn't re-run but the wishlist may have changed on another page
    window.addEventListener('pageshow', this.#onWishlistUpdate);
    this.renderCount();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    window.removeEventListener('pageshow', this.#onWishlistUpdate);
  }

  renderCount() {
    const count = Wishlist.getProductIds().length;

    this.refs.count.textContent = count > 99 ? '99+' : count.toString();
    this.refs.count.classList.toggle('visually-hidden', count === 0);
  }
}

if (!customElements.get('wishlist-header-icon')) {
  customElements.define('wishlist-header-icon', WishlistHeaderIconComponent);
}
